




































































import { PinFilterHelper, RouteHelpers } from '@/mixins';
import { ColumnsModel, ComponentConstants, Dictionary, ErrorCode, Filter, RouteRegEx, Workflow, WorkflowArchiveQueueFilterModel, WorkflowQueueDataModel } from '@/types';
import Vue from 'vue';
import VueRouter from 'vue-router';
import { mapActions, mapGetters } from 'vuex';
import MStoreSpinnerButton from '../shared/MStoreSpinnerButton.vue';
import MStoreTable from '../shared/Table/MStoreTable.vue';

export default Vue.extend({
  name: ComponentConstants.Workflow.ArchiveQueue,
  mixins: [RouteHelpers, PinFilterHelper],
  props: {
    workflowQueueDataModel: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      filterFields: [] as Filter[],
      isSearching: false
    };
  },
  methods: {
    ...mapActions('workflowQueueStore', ['resetWorkflowQueueState', 'setTableStateField', 'setNextJobsList', 'setAllJobsList', 'setArchiveQueueFilters']),
    ...mapActions('userPreferencesStore', ['setUserPreferedWorkflowQueueTableOptions']),
    getWorkflowQueue(this: any) {
      this.$emit('get-workflow-queue', this.workflowQueueDataModel.queueId);
    },
    getWorkflowJob(item: any) {
      this.$emit('get-workflow-job', item.jobid);
    },
    onTableStateFieldUpdated(this: any, field: string, value: any) {
      this.setTableStateField({
        queueId: this.workflowQueueDataModel.queueId,
        field,
        value
      });
    },
    onUserPreferedTableStateFieldUpdated(this: any, field: string, value: any) {
      this.setUserPreferedWorkflowQueueTableOptions({
        queueId: this.workflowQueueDataModel.queueId,
        field,
        value
      });
    },
    initialise(this: any) {
      const canKeepState = this.canKeepState(RouteRegEx.WorkflowQueue) || this.workflowQueueDataModel.queueId.toString() === this.$route.params?.id;
      if (!canKeepState) {
        this.resetWorkflowQueueState(this.workflowQueueDataModel.queueId);
        this.setNextJobs(this.workflowQueueDataModel.queueData);
        this.setAllJobs(this.workflowQueueDataModel.queueData);
      } else {
        this.filterFields = this.getArchiveQueueFilters.filterFields;
      }
    },
    setAllJobs(this: any, items: any) {
      this.setAllJobsList(items.map((job: any) => job.jobid));
    },
    setNextJobs(this: any, items: any) {
      this.setNextJobsList(items.map((job: any) => job.jobid));
    },
    updateFilterValue(index: number, value: string) {
      Vue.set(this.filterFields, index, { ...(this.filterFields as Filter[])[index], value });
    },
    updateFilterKey(index: number, key: string) {
      Vue.set(this.filterFields, index, { ...(this.filterFields as Filter[])[index], key });
    },
    addFilter(this: any) {
      this.filterFields = [...(this.filterFields as Filter[]), { key: '', value: '' }] as Filter[];
    },
    clearFilters(this: any) {
      const filterFields: Filter[] = this.filterFields;
      this.filterFields = filterFields.map(({ key }) => ({
        key,
        value: ''
      }));
      this.setArchiveQueueFilters({ filterFields: this.filterFields, filterDictionary: this.filterDictionary } as WorkflowArchiveQueueFilterModel);
    },
    resetFilters(this: any) {
      this.filterFields = [{ ...this.DEFAULT_FILTER_OPTION }];
      this.setArchiveQueueFilters({ filterFields: this.filterFields, filterDictionary: this.filterDictionary } as WorkflowArchiveQueueFilterModel);
    },
    getFilterOptions(this: any, key: string) {
      const selectedFilterColumn = (this.workflowQueueDataModel as WorkflowQueueDataModel).queueColumns.find(({ key: columnKey }) => key === columnKey) ?? this.DEFAULT_FILTER_OPTION;
      const selectedFilterOption = {
        value: key,
        text: selectedFilterColumn?.label,
        disabled: selectedFilterColumn?.disabled
      };
      return [selectedFilterOption, ...this.unusedFilterOptions];
    },
    removeFilter(this: any, index: number) {
      this.filterFields.splice(index, 1);
    },
    async applyFilters(this: any) {
      await this.setArchiveQueueFilters({ filterFields: this.filterFields, filterDictionary: this.filterDictionary } as WorkflowArchiveQueueFilterModel);
      this.getWorkflowQueue();
    }
  },
  computed: {
    ...mapGetters('workflowQueueStore', ['getTableStateField', 'getArchiveQueueFilters']),
    ...mapGetters('userPreferencesStore', ['getUserPreferedTableStateField']),
    emptyText(this: any): string {
      return this.workflowQueueDataModel.hasNotification ? this.workflowQueueDataModel.notification : 'There are no records to show';
    },
    tableStateField(this: any) {
      return (field: string) => this.getTableStateField(this.workflowQueueDataModel.queueId, field);
    },
    userPreferedTableStateField(this: any) {
      return (field: string) => this.getUserPreferedTableStateField(this.workflowQueueDataModel.queueId, field);
    },
    emptyHtml(this: any) {
      if (this.workflowQueueDataModel.errorCode === ErrorCode.SqlTimeOutException) {
        return `<div class="alert alert-danger rounded-0 font-weight-bold">
                  ${this.workflowQueueDataModel.notification}</div>`;
      }
    },
    unusedFilterOptions(this: any) {
      return [
        ...(this.workflowQueueDataModel.queueColumns as ColumnsModel[])
          .filter(({ key: columnKey }) => !(this.filterFields as Filter[]).find(({ key: filterKey }) => columnKey === filterKey))
          .map(({ key, label }) => ({
            value: key,
            text: label
          }))
      ];
    },
    multiFilter(this: any) {
      return (this.filterFields as Filter[]).length > 1;
    },
    maxFiltersReached(this: any) {
      return (this.filterFields as Filter[]).length === this.workflowQueueDataModel.queueColumns.length;
    },
    filtersCanBeReset(this: any) {
      return this.multiFilter || (this.filterFields as Filter[]).some(({ key, value }) => key || value);
    },
    filtersCanBeCleared(this: any) {
      return (this.filterFields as Filter[]).some(({ value }) => value);
    },
    filterDictionary(this: any) {
      return (this.filterFields as Filter[])
        .filter((filterField) => filterField.key && filterField.value)
        .reduce(
          (filterDictionary, filterField) => ({
            ...filterDictionary,
            [filterField.key]: filterField.value
          }),
          {} as Dictionary<string>
        );
    }
  },
  mounted(this: any) {
    this.filterFields = [{ ...this.DEFAULT_FILTER_OPTION }];
    this.initialise();
  },
  created(this: any) {
    this.DEFAULT_FILTER_OPTION = { key: '', label: 'Select Field', disabled: true };
  },
  beforeDestroy() {
    const toRouteName = this.$route.name?.toLowerCase();
    const toRouteParams = this.$route.params;
    const currentQueueId = (this.workflowQueueDataModel as WorkflowQueueDataModel).queueId;
    const canKeepState = toRouteName?.match(RouteRegEx.WorkflowQueue) || (toRouteName === Workflow.Queue.name.toLowerCase() && Number(toRouteParams?.id) === currentQueueId);
    if (!canKeepState) {
      this.resetWorkflowQueueState(this.workflowQueueDataModel.queueId);
    }
  },
  components: {
    MStoreTable,
    MStoreSpinnerButton
  }
});
